import { useEmbarcacaoMotores } from "@/modules/embarcacoes";
import type { EmbarcacaoMotor, EmbarcacaoMotoresField } from "@/modules/embarcacoes";
import type { EmptyStateContent } from "@/screens/control-panel/components/empty-state";
import {
  DetailField,
  DetailSection,
  DetailSectionFields,
  DetailSectionState,
} from "../detail-section";
import { formatDetailDate, formatDetailNumber } from "../embarcacao-detail-format";
import type { EmbarcacaoDetailSectionProps } from "../embarcacao-detail-section";
import styles from "./motores-section.module.scss";

export const MOTORES_ID = "motores";
export const MOTORES_TITLE = "Motores";
export const MOTORES_LOADING_MESSAGE = "Carregando os Motores da embarcação...";

/** Título do bloco quando o motor chega sem identificação, para diferenciá-lo (AC2). */
export const MOTOR_FALLBACK_LABEL = "Motor";

/** Padrão global de estado vazio do Hub para a embarcação sem motores (AC4). */
const MOTORES_EMPTY_STATE: EmptyStateContent = {
  title: "Nenhuma informação de Motores disponível",
  description: "Assim que houver dados de motor para esta embarcação, eles aparecerão nesta seção.",
};

const TEST_ID = "embarcacao-motores";

/** Como o valor de um campo é apresentado: número e data têm tratamento próprio. */
type MotorFieldKind = "text" | "number" | "date";

interface MotorFieldDefinition {
  readonly key: EmbarcacaoMotoresField;
  readonly label: string;
  readonly kind: MotorFieldKind;
}

/**
 * Os 12 campos de cada bloco, na ordem e nas colunas do protótipo (AC3). O grid
 * os distribui em duas colunas, preenchendo cada linha da esquerda para a
 * direita. A identificação do motor (`motor`) encabeça o bloco e a data da
 * última atualização acompanha o título da seção.
 */
export const MOTORES_FIELDS: MotorFieldDefinition[] = [
  { key: "marca", label: "Marca", kind: "text" },
  { key: "modelo", label: "Modelo", kind: "text" },
  { key: "numeroSerie", label: "N° de série", kind: "text" },
  { key: "codigoTmMaster", label: "Código TM Master", kind: "text" },
  {
    key: "empresaAutomacaoResponsavel",
    label: "Empresa de automação responsável do motor",
    kind: "text",
  },
  { key: "potencia", label: "Potência (Kw)", kind: "number" },
  { key: "rpm", label: "RPM", kind: "number" },
  { key: "tbo", label: "TBO", kind: "number" },
  { key: "periodicidade", label: "Periodicidade", kind: "number" },
  { key: "ativo", label: "Ativo?", kind: "text" },
  { key: "ordemMotor", label: "Ordem Motor", kind: "number" },
  { key: "atualizacaoMotores", label: "Data Atualização Motores", kind: "date" },
];

/** Valor do campo pronto para a tela; `null` segue como campo sem preenchimento (AC3). */
function fieldValue(motor: EmbarcacaoMotor, { key, kind }: MotorFieldDefinition): string | null {
  const value = motor[key];
  if (value === null) return null;

  if (kind === "number")
    return typeof value === "number" ? formatDetailNumber(value) : String(value);
  if (kind === "date") return typeof value === "string" ? formatDetailDate(value) : String(value);

  return typeof value === "string" ? value : String(value);
}

/**
 * Ordena os motores pela Ordem Motor, do menor para o maior (RN002). Os motores
 * sem ordem vão para o fim, preservando a ordem em que a fonte os devolveu.
 */
function byOrdemMotor(first: EmbarcacaoMotor, second: EmbarcacaoMotor): number {
  if (first.ordemMotor === null && second.ordemMotor === null) return 0;
  if (first.ordemMotor === null) return 1;
  if (second.ordemMotor === null) return -1;

  return first.ordemMotor - second.ordemMotor;
}

/**
 * Data de última atualização da seção, exibida junto ao título como nas demais
 * seções: a mais recente entre os motores. Nenhum valor é derivado — apenas se
 * escolhe, entre as datas recebidas, a que representa a última atualização.
 */
function sectionUltimaAtualizacao(motores: EmbarcacaoMotor[]): string | null {
  let latest: string | null = null;
  let latestTime = Number.NEGATIVE_INFINITY;

  for (const { ultimaAtualizacao } of motores) {
    if (ultimaAtualizacao === null) continue;

    const time = Date.parse(ultimaAtualizacao);
    if (Number.isNaN(time)) {
      if (latest === null) latest = ultimaAtualizacao;
      continue;
    }

    if (time > latestTime) {
      latestTime = time;
      latest = ultimaAtualizacao;
    }
  }

  return latest;
}

/**
 * Seção Motores do detalhamento da embarcação (US-1800).
 *
 * É a primeira seção com vários registros: cada motor vira um bloco próprio,
 * encabeçado pela sua identificação e com os demais campos em duas colunas
 * (AC3). Os blocos aparecem em ordem crescente de Ordem Motor (AC2/RN002).
 * Exclusivamente de leitura (RN003): nenhum botão ou campo de cadastro, edição
 * ou exclusão, e sem ordenação, filtro ou exportação.
 */
export function MotoresSection({ embarcacaoId, onReauthenticate }: EmbarcacaoDetailSectionProps) {
  const { status, motores, errorMessage, reload } = useEmbarcacaoMotores(embarcacaoId);
  const orderedMotores = motores ? [...motores].sort(byOrdemMotor) : null;

  return (
    <DetailSection
      id={MOTORES_ID}
      title={MOTORES_TITLE}
      // Só há data de atualização a exibir quando a seção tem motores.
      ultimaAtualizacao={orderedMotores ? sectionUltimaAtualizacao(orderedMotores) : undefined}
      testId={TEST_ID}
    >
      {orderedMotores ? (
        <div className={styles.blocks} data-testid={`${TEST_ID}-blocks`}>
          {orderedMotores.map((motor, index) => {
            const blockTestId = `${TEST_ID}-block-${index}`;
            const heading = motor.motor ?? `${MOTOR_FALLBACK_LABEL} ${index + 1}`;

            return (
              <article key={blockTestId} className={styles.block} data-testid={blockTestId}>
                <h3 className={styles.blockTitle} data-testid={`${blockTestId}-motor`}>
                  {heading}
                </h3>

                <DetailSectionFields testId={`${blockTestId}-fields`}>
                  {MOTORES_FIELDS.map((field) => (
                    <DetailField
                      key={field.key}
                      label={field.label}
                      value={fieldValue(motor, field)}
                      testId={`${blockTestId}-${field.key}`}
                    />
                  ))}
                </DetailSectionFields>
              </article>
            );
          })}
        </div>
      ) : (
        <DetailSectionState
          // `success` sempre traz os blocos: aqui só chegam carregamento e exceções.
          status={status === "success" ? "error" : status}
          loadingMessage={MOTORES_LOADING_MESSAGE}
          errorMessage={errorMessage}
          emptyContent={MOTORES_EMPTY_STATE}
          onReauthenticate={onReauthenticate}
          onRetry={reload}
          testId={TEST_ID}
        />
      )}
    </DetailSection>
  );
}
